import { Instagram, Facebook, Youtube } from "lucide-react";
import { openExternal } from "@/lib/open-external";

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 8.5a7.5 7.5 0 0 1-4.5-1.5v8.25a6.25 6.25 0 1 1-6.25-6.25c.34 0 .67.03 1 .08v3.18a3.1 3.1 0 1 0 2.17 2.96V2h3.08A4.42 4.42 0 0 0 21 5.42V8.5Z" />
    </svg>
  );
}

function PinterestIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M11 8.5c2.2-.4 4.5.9 4.5 3.4 0 2.3-1.6 3.9-3.4 3.9-1 0-1.7-.5-1.9-1.1M10.5 21l1.8-7.5" />
    </svg>
  );
}

const SOCIALS = [
  {
    name: "Instagram",
    handle: "@recoverytrauma_",
    url: "https://www.instagram.com/recoverytrauma_",
    Icon: Instagram,
    background:
      "radial-gradient(circle at 30% 110%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)",
  },
  {
    name: "Facebook",
    handle: "Recovery Trauma",
    url: "https://www.facebook.com/people/Recovery-Trauma/61568168060890",
    Icon: Facebook,
    background: "#1877F2",
  },
  {
    name: "Pinterest",
    handle: "@recoverytrauma",
    url: "https://www.pinterest.com/recoverytrauma",
    Icon: PinterestIcon,
    background: "#E60023",
  },
  {
    name: "TikTok",
    handle: "@recoverytrauma",
    url: "https://www.tiktok.com/@recoverytrauma",
    Icon: TikTokIcon,
    background: "#010101",
  },
  {
    name: "YouTube",
    handle: "@RecoveryTrauma",
    url: "https://www.youtube.com/@RecoveryTrauma",
    Icon: Youtube,
    background: "#FF0000",
  },
];


export function SocialFollowSection() {
  return (
    <section
      aria-labelledby="social-follow-heading"
      className="relative mt-0 mb-1 px-4 pt-1 pb-3 sm:px-8 sm:pt-2 sm:pb-4 lg:rounded-[28px] lg:border lg:border-gold/25 lg:bg-gold-light/20 lg:px-10 lg:py-7"
    >
      <div className="relative lg:grid lg:grid-cols-[minmax(0,1.2fr)_minmax(20rem,0.8fr)] lg:items-center lg:gap-10">
        <div>
        <h2
          id="social-follow-heading"
          className="text-center font-display text-xl leading-tight tracking-tight text-foreground sm:text-2xl lg:text-start lg:font-['Outfit'] lg:text-4xl lg:font-semibold"
        >
          Join 380,000+ following Recovery Trauma
          <span className="text-[var(--gold-deep)]">™</span>
        </h2>

        <p className="mx-auto mt-2 max-w-xl text-center text-xs leading-relaxed text-foreground/70 sm:text-sm lg:mx-0 lg:text-start lg:font-['Figtree'] lg:text-base">
          Daily trauma education, nervous system healing, recovery tools,
          community support and behind-the-scenes updates.
        </p>

        </div>
        <div
          role="region"
          aria-label="Recovery Trauma social media"
          className="mx-auto mt-4 grid w-full max-w-[320px] grid-cols-5 place-items-center gap-x-2 gap-y-0 sm:mt-5 sm:max-w-[360px] sm:gap-x-3 lg:mt-0 lg:max-w-[440px] lg:gap-x-5"
        >
          {SOCIALS.map(({ name, handle, url, Icon, background }) => (
            <a
              key={name}
              href={url}
              onClick={(e) => openExternal(url, e)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Follow Recovery Trauma on ${name} (${handle})`}
              title={`${name} · ${handle}`}
              className="group flex w-full flex-col items-center gap-1.5 focus:outline-none"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center sm:h-12 sm:w-12">
                <span
                  style={{ background }}
                  className="flex h-9 w-9 items-center justify-center rounded-lg text-white shadow-[0_3px_10px_-4px_rgba(0,0,0,0.2)] ring-1 ring-[var(--gold-deep)]/40 transition-all duration-300 ease-out group-hover:-translate-y-0.5 group-hover:shadow-[0_6px_16px_-8px_rgba(184,134,11,0.55)] group-hover:ring-[var(--gold-deep)] group-active:scale-95 group-focus-visible:ring-2 group-focus-visible:ring-[var(--gold-deep)]/60 sm:h-10 sm:w-10"
                >
                  <Icon className="h-4.5 w-4.5 sm:h-5 sm:w-5" strokeWidth={1.8} />
                </span>
              </span>

              <span className="block w-full text-center text-[10px] font-medium leading-none tracking-wide text-foreground/70 transition-colors group-hover:text-[var(--gold-deep)]">
                {name}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export default SocialFollowSection;
